// Versioned localStorage store for the static build. The ONLY durable user data is
// vocab/SRS (the words they know + review schedule); reading position is minor; glosses
// are a personal extra. Content updates (timings/stories) ship in the bundle and never
// touch this store. The schema is VERSIONED from day one so the rare shape change migrates
// cleanly — back up first, idempotent steps — and known words are never lost.
//
// Keys (origin-scoped; stable forever on seediq-reader.netlify.app):
//   seediq_schema_version   "1"
//   seediq_vocab_v1         { "<dialect>:<word>": { status, ...SM-2 fields } }   ← the precious one
//   seediq_progress_v1      { textId: itemIndex }
//   seediq_gloss_v1         { "<word>": "中文" }   (personal gloss edits; editing off in static)

const SCHEMA_KEY = 'seediq_schema_version';
const SCHEMA_VERSION = 1;

export const VOCAB_KEY = 'seediq_vocab_v1';
export const PROGRESS_KEY = 'seediq_progress_v1';
export const GLOSS_KEY = 'seediq_gloss_v1';

function read(key, fallback) {
  try { const v = localStorage.getItem(key); return v == null ? fallback : JSON.parse(v); }
  catch { return fallback; }
}
function write(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* quota / private mode */ }
}

// ── migration ──────────────────────────────────────────────────────────────
// Runs once at module load. Bumps the stored schema to SCHEMA_VERSION via ordered,
// idempotent steps. Always snapshots the current data first so a bad migration is
// recoverable. Add `if (v < N) { ...; v = N; }` blocks for future shape changes.
function migrate() {
  let v = parseInt(localStorage.getItem(SCHEMA_KEY) || '0', 10) || 0;
  if (v === SCHEMA_VERSION) return;

  try {
    const snap = {};
    for (const k of [VOCAB_KEY, PROGRESS_KEY, GLOSS_KEY]) {
      const raw = localStorage.getItem(k);
      if (raw != null) snap[k] = raw;
    }
    if (Object.keys(snap).length) {
      localStorage.setItem(`seediq_premigrate_${Date.now()}`, JSON.stringify(snap));
    }
  } catch { /* best effort */ }

  if (v < 1) {
    // 0 -> 1: adopt the v1 keys. Rescue any pre-static localStorage left by the
    // old backend-era build ('seediq_vocab') so early testers keep their words.
    if (localStorage.getItem(VOCAB_KEY) == null) {
      const legacy = read('seediq_vocab', null);
      if (legacy && typeof legacy === 'object') write(VOCAB_KEY, legacy);
    }
    v = 1;
  }
  // future: if (v < 2) { ...; v = 2; }

  localStorage.setItem(SCHEMA_KEY, String(SCHEMA_VERSION));
}
migrate();

// ── vocab (status + SRS) ─────────────────────────────────────────────────────
export const vocabStore = {
  getAll: () => read(VOCAB_KEY, {}),
  // Merge a patch into one "dialect:word" entry; returns the new full map.
  // Stamps updatedAt so vocabSync.js's cloud merge can tell cards apart.
  setKey: (key, patch) => {
    const all = read(VOCAB_KEY, {});
    all[key] = { ...all[key], ...patch, updatedAt: new Date().toISOString() };
    write(VOCAB_KEY, all);
    return all;
  },
  // Full-replace with a cloud-merged map (vocabSync.js pull/push response).
  replaceKeys: (entries) => {
    write(VOCAB_KEY, entries);
    return entries;
  },
};

// ── reading position ─────────────────────────────────────────────────────────
export const progressStore = {
  getAll: () => read(PROGRESS_KEY, {}),
  set: (textId, index) => {
    const all = read(PROGRESS_KEY, {});
    all[textId] = index;
    write(PROGRESS_KEY, all);
    return all;
  },
};

// ── personal glosses ─────────────────────────────────────────────────────────
export const glossStore = {
  getAll: () => read(GLOSS_KEY, {}),
  set: (word, gloss) => {
    const all = read(GLOSS_KEY, {});
    const k = (word || '').toLowerCase();
    if (gloss == null || gloss === '') delete all[k]; else all[k] = gloss;
    write(GLOSS_KEY, all);
    return all;
  },
};
